/**
 * InvoiceLinesForm Component
 *
 * Controlled editor for invoice line items. Renders an add-line toolbar, a
 * responsive CSS-Grid line list (single column on mobile, one column per input
 * on desktop), inline validation annotations on fields and lines, and a
 * top-level error banner for issues whose `path` is empty (e.g. save-level
 * errors).
 *
 * No client-side VAT calculations — all amount fields are persisted as
 * entered; the backend is the source of truth.
 *
 * Line rows are memoized: every row callback is referentially stable (mutable
 * inputs live behind `latestRef`), so a keystroke re-renders only the edited
 * row instead of the whole list.
 *
 * Uses `useTranslation('modules.modals.editInvoiceLines')` for all labels.
 */
import * as React from 'react';
import { parseDecimal, roundToCurrencyPrecision, type Decimal } from '@insio/decimal';
import type { CurrencyCode } from '@insio/shared-types';
import { Button } from '../../../components/Button';
import { type VatRateOption } from '../../../components/inputs';
import { Icon } from '../../../components/icons/Icon';
import { ErrorSummary } from '../../../components/errors';
import { Toggle } from '../../../components/Toggle';
import { useTranslation } from '../../../providers/UIProvider';
import {
  useInvoiceCurrencyCode,
  useInvoiceIsVatDocument
} from '../../../providers/InvoiceProvider';
import { cn, getTestId, withRoundedExpected, type TestIdProps } from '../../../utils';
import type { InvoiceLine, InvoiceLineAmounts } from '../../../graphql/generated/graphql';
import type { ValidationIssue } from '../../../types';
import {
  createBlankLine,
  computeMismatchFields,
  normalizeLine,
  updateLineField,
  withNumericBuffer,
  type ComputableField
} from './utils';
import {
  lineFingerprint,
  useNumericInputs,
  useValidationIssueLookup,
  useLineAmountsRecompute,
  useClearedFields,
  type RecomputeInvoiceLineFn
} from './hooks';
import { Virtuoso, type VirtuosoHandle } from 'react-virtuoso';
import { InvoiceLine as InvoiceLineRow } from './InvoiceLine';
import type { InvoiceLineExtraField } from './invoice-line-extra-field';

export type { RecomputeInvoiceLineFn } from './hooks';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type { VatRateOption, InvoiceLine };
export type { InvoiceLineExtraField, InvoiceLineExtraFieldProps } from './invoice-line-extra-field';

/** Form-state shape: a server-provided InvoiceLine (with `id`), or a locally added blank one
 * without `id` whose local `key` identifies the row in the UI. */
export type InvoiceLineFormItem = Partial<InvoiceLine> & { key?: string };

export interface InvoiceLinesFormData {
  invoiceLines: InvoiceLineFormItem[];
  /** Whether unit prices on all lines are entered as gross (VAT-inclusive)
   * or net. Form-level flag — the per-line `isUnitPriceInclVat` field is
   * deprecated in favor of this form-wide value. */
  isUnitPriceInclVat: boolean;
}

/**
 * Throw from a host's save callback to signal server-side validation failures
 * for individual invoice lines. The wrapping host (e.g. `EditInvoiceLinesModal`)
 * extracts `issues` and forwards them to this form as `errors`, and re-throws
 * so the host's generic save-error banner still fires. Each issue uses a
 * dot-path like `invoiceLines.<index>.<field>` (or `invoiceLines.<index>` for line-level).
 */
export class InvoiceLinesValidationError extends Error {
  readonly issues: ValidationIssue[];

  constructor(message: string, issues: ValidationIssue[]) {
    super(message);
    this.name = 'InvoiceLinesValidationError';
    this.issues = issues;
  }
}

export interface InvoiceLinesFormProps extends TestIdProps {
  value: InvoiceLineFormItem[];
  /** Whether unit prices are entered gross (VAT-inclusive) or net. Form-level
   * flag; the per-line `isUnitPriceInclVat` field on `InvoiceLine` is
   * deprecated and ignored by this form. */
  isUnitPriceInclVat: boolean;
  /** Emits whenever lines or the `isUnitPriceInclVat` toggle change. */
  onChange: (data: InvoiceLinesFormData) => void;
  /** Validation issues. Issues in the `invoiceLines.<index>[.<field>]`
   * namespace are mapped to per-line/per-field annotations; issues with an
   * empty `path` are rendered as a top-level error banner. Other issues are
   * ignored. */
  errors?: ValidationIssue[];
  /** Available VAT rate options for dropdown. When provided, vatRate renders as Select; otherwise as text Input. */
  vatRateOptions?: readonly VatRateOption[];
  disabled?: boolean;
  /** Optional async hook that resolves backend-computed `InvoiceLineAmounts`
   * for a single line. When provided the form fires it (debounced) while the
   * user types and immediately on blur; resulting amounts are surfaced as
   * "expected" hints next to any derivable blank/disagreeing input (`unitPrice`,
   * `vatAmount`, `totalAmountNoVat`, `totalAmountInclVat`), and a per-line
   * recompute button fills the blank ones. */
  onRecomputeLine?: RecomputeInvoiceLineFn;
  /** Debounce window for change-driven recomputes. Defaults to 400 ms. */
  recomputeDebounceMs?: number;
  /**
   * When the form is inside a custom scroll container (e.g. the ScanEditor
   * panel), pass a ref to that element so the virtualizer scrolls correctly.
   * Omit when the form is inside a modal that scrolls with the window.
   */
  scrollerRef?: React.RefObject<HTMLElement | null>;
  /** Host fields drawn on every line under its amounts. Memoize the array: rows re-render when it changes. */
  lineFields?: readonly InvoiceLineExtraField[];
}

/** Identity of a row across re-renders and deletes. */
const lineIdentity = (line: InvoiceLineFormItem) => line.id ?? line.key;

interface CapturedLine {
  identity: string | undefined;
  fingerprint: string;
}

/** A throwing host `onChange` inside an async action must not become an unhandled rejection. */
const logActionError = (error: unknown) => {
  // eslint-disable-next-line no-console
  console.error('Invoice line recompute action failed:', error);
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export const InvoiceLinesForm: React.FC<InvoiceLinesFormProps> = ({
  value,
  isUnitPriceInclVat,
  onChange,
  errors = [],
  vatRateOptions,
  disabled = false,
  onRecomputeLine,
  recomputeDebounceMs,
  scrollerRef,
  lineFields,
  testId,
  id
}) => {
  const virtuosoRef = React.useRef<VirtuosoHandle>(null);
  const t = useTranslation('modules.modals.editInvoiceLines');
  const currencyCode = (useInvoiceCurrencyCode() ?? undefined) as CurrencyCode | undefined;

  const isVatDocument = useInvoiceIsVatDocument();
  const recompute = useLineAmountsRecompute({
    recompute: onRecomputeLine,
    isUnitPriceInclVat,
    debounceMs: recomputeDebounceMs
  });
  const { runNow: runRecomputeNow, ensureFresh, adopt } = recompute;

  const { clearedLines, markLineCleared } = useClearedFields(errors);

  // Latest mutable inputs for the stable row callbacks below. Reading through
  // the ref keeps every callback's identity constant across keystrokes, which
  // is what lets `React.memo(InvoiceLine)` skip untouched rows.
  const latest = {
    value,
    isUnitPriceInclVat,
    onChange,
    onRecomputeLine,
    disabled,
    isVatDocument,
    currencyCode
  };
  const latestRef = React.useRef(latest);
  latestRef.current = latest;

  const mountedRef = React.useRef(true);
  React.useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  // Re-fire recompute for every line when the form-level inclVat flag flips —
  // changing the toggle re-interprets `unitPrice` (gross vs net), so previously
  // computed amounts are stale until the backend recalculates. We skip the
  // initial render: this only reacts to actual user-driven toggle changes.
  // The hook's own `inclVatRef`-sync effect runs before this one (declared
  // first), so `runNow` sees the new flag value when it fires.
  const previousInclVatRef = React.useRef(isUnitPriceInclVat);
  React.useEffect(() => {
    if (previousInclVatRef.current === isUnitPriceInclVat) return;
    previousInclVatRef.current = isUnitPriceInclVat;
    if (!onRecomputeLine) return;
    value.forEach((line, index) => runRecomputeNow(index, line));
  }, [isUnitPriceInclVat, value, onRecomputeLine, runRecomputeNow]);

  // Run recompute once per line so expected-amount warnings appear immediately
  // when the modal opens with pre-existing lines, without waiting for the user
  // to edit a field. Tracking is per line object rather than positional index,
  // since index tracking would go stale across deletes (e.g. deleting line 0
  // and adding a new line at index 0 would skip the new line's initial
  // recompute). Lines reopened from a just-saved committed payload are new
  // objects, so they still get an initial recompute instead of being skipped
  // (which would leave the recompute buttons stuck disabled until a page refresh).
  // Subsequent value changes for already-recomputed lines are ignored here,
  // leaving the debounced edit/blur recompute flow untouched.
  const initiallyRecomputedRef = React.useRef<WeakSet<InvoiceLineFormItem>>(new WeakSet());
  React.useEffect(() => {
    if (!onRecomputeLine) return;
    value.forEach((line, index) => {
      if (initiallyRecomputedRef.current.has(line)) return;
      initiallyRecomputedRef.current.add(line);
      runRecomputeNow(index, line);
    });
  }, [onRecomputeLine, runRecomputeNow, value]);

  const emit = React.useCallback(
    (nextLines: InvoiceLineFormItem[], nextIsUnitPriceInclVat?: boolean) => {
      const current = latestRef.current;
      const inclVat = nextIsUnitPriceInclVat ?? current.isUnitPriceInclVat;
      // Seen by the next action in the same task; the next render overwrites it.
      latestRef.current = { ...current, value: nextLines, isUnitPriceInclVat: inclVat };
      current.onChange({ invoiceLines: nextLines, isUnitPriceInclVat: inclVat });
    },
    []
  );

  const applyUpdate = React.useCallback(
    (
      index: number,
      field: keyof InvoiceLine,
      raw: string | Decimal | null
    ): InvoiceLineFormItem => {
      const next = [...latestRef.current.value];
      next[index] = updateLineField(next[index], field, raw);
      emit(next);
      return next[index];
    },
    [emit]
  );

  /** A host line field's change: merged as is, amounts untouched, so no recompute. */
  const patchLine = React.useCallback(
    (index: number, patch: Partial<InvoiceLine>) => {
      const next = [...latestRef.current.value];
      next[index] = { ...next[index], ...patch };
      emit(next);
    },
    [emit]
  );

  const updateLine = React.useCallback(
    (index: number, field: keyof InvoiceLine, raw: string | Decimal | null) => {
      const updated = applyUpdate(index, field, raw);
      // VAT selector / text-field edits commit synchronously here; numeric
      // inputs route through `useNumericInputs.onCommit` instead (on blur).
      recompute.schedule(index, updated);
    },
    [applyUpdate, recompute.schedule]
  );

  const numericInputs = useNumericInputs({
    onCommit: (index, field, raw) => {
      const updated = applyUpdate(index, field, raw);
      // Blur => fire immediately so the warning hint catches up without
      // waiting on the debounce window.
      recompute.runNow(index, updated);
    }
  });

  /** Keystroke in a numeric input: buffer the text, no `emit`; schedule a recompute of the
   * tentative line, built like the rendered one (`withNumericBuffer`): same fingerprint. */
  const handleNumericChange = React.useCallback(
    (index: number, field: keyof InvoiceLine, raw: string) => {
      numericInputs.handleChange(index, field, raw);
      const { value: lines, onRecomputeLine: canRecompute } = latestRef.current;
      const current = lines[index];
      if (!canRecompute || !current) return;
      recompute.schedule(index, withNumericBuffer(current, numericInputs.peekLineBuffer(index)));
    },
    [numericInputs.handleChange, numericInputs.peekLineBuffer, recompute.schedule]
  );

  const filteredErrors = React.useMemo(() => {
    const rounded = errors.map(issue => withRoundedExpected(issue, currencyCode));
    if (clearedLines.size === 0) return rounded;
    return rounded.filter(issue => {
      if (!issue.path) return true;
      const m = /^invoiceLines\.(\d+)(?:\.|$)/.exec(issue.path);
      if (!m) return true;
      return !clearedLines.has(Number(m[1]));
    });
  }, [errors, clearedLines, currencyCode]);

  const issues = useValidationIssueLookup(filteredErrors);

  // Carry quantity, unit + VAT rate from the last line — invoices commonly repeat
  // them across items, so this saves re-entry on every new line.
  const pendingFocusLineKeyRef = React.useRef<string | null>(null);
  const addLine = () => {
    const previous = value[value.length - 1];
    const newLine = {
      ...createBlankLine(),
      quantity: previous?.quantity ?? null,
      unit: previous?.unit ?? null,
      vatRate: previous?.vatRate ?? null
    };
    pendingFocusLineKeyRef.current = newLine.key ?? null;
    emit([...value, newLine]);
  };

  // After a new line renders, scroll it into view then focus its name field.
  // Keyed by the local row key so it survives reordering between emit and re-render.
  React.useEffect(() => {
    const key = pendingFocusLineKeyRef.current;
    if (key == null) return;
    const index = value.findIndex(line => line.key === key);
    if (index === -1) return;
    pendingFocusLineKeyRef.current = null;
    virtuosoRef.current?.scrollToIndex({ index, behavior: 'auto' });
    // Focus after a short delay to let Virtuoso render the row.
    requestAnimationFrame(() => {
      document.getElementById(`description-${index}`)?.focus();
    });
  }, [value]);

  const deleteLine = React.useCallback(
    (index: number) => {
      emit(latestRef.current.value.filter((_, i) => i !== index));
      numericInputs.clearForLine(index);
      recompute.clearForLine(index);
    },
    [emit, numericInputs.clearForLine, recompute.clearForLine]
  );

  const toggleUnitPriceInclVat = (next: boolean) => emit(value, next);

  /** Lines whose action awaits a fresh result; another action on them is ignored. */
  const busyLinesRef = React.useRef(new Set<number>());

  const fingerprintOf = React.useCallback(
    (line: InvoiceLineFormItem) => lineFingerprint(line, latestRef.current.isUnitPriceInclVat),
    []
  );

  /** Settle the result just written for `updated`, then verify it with a request. */
  const afterFreshWrite = React.useCallback(
    (index: number, updated: InvoiceLineFormItem, fresh: InvoiceLineAmounts) => {
      initiallyRecomputedRef.current.add(updated);
      adopt(index, updated, fresh);
      runRecomputeNow(index, updated);
    },
    [adopt, runRecomputeNow]
  );

  /** What an action remembers of a line before awaiting its fresh result. */
  const captureLine = React.useCallback(
    (line: InvoiceLineFormItem): CapturedLine => ({
      identity: lineIdentity(line),
      fingerprint: fingerprintOf(line)
    }),
    [fingerprintOf]
  );

  /** Whether the line at `index` is still the captured one: same row, same inputs
   * (inclVat flag included), and no text typed into it meanwhile. */
  const isUnchangedSince = React.useCallback(
    (index: number, current: InvoiceLineFormItem | undefined, captured: CapturedLine) =>
      !!current &&
      lineIdentity(current) === captured.identity &&
      fingerprintOf(current) === captured.fingerprint &&
      numericInputs.peekLineBuffer(index) === undefined,
    [fingerprintOf, numericInputs.peekLineBuffer]
  );

  /** Await amounts for the line's current inputs, then write `apply`'s line. Nothing is written
   * when the form unmounted or got disabled, the request failed, or the line changed. */
  const runLineAction = React.useCallback(
    async (
      index: number,
      apply: (current: InvoiceLineFormItem, fresh: InvoiceLineAmounts) => InvoiceLineFormItem
    ): Promise<void> => {
      const busy = busyLinesRef.current;
      const line = latestRef.current.value[index];
      if (!line || busy.has(index)) return;
      busy.add(index);
      try {
        const captured = captureLine(line);
        const fresh = await ensureFresh(index, line);
        if (!fresh || !mountedRef.current || latestRef.current.disabled) return;
        const current = latestRef.current.value[index];
        if (!current || !isUnchangedSince(index, current, captured)) return;
        const updated = apply(current, fresh);
        if (updated === current) return;
        const next = [...latestRef.current.value];
        next[index] = updated;
        emit(next);
        afterFreshWrite(index, updated, fresh);
      } finally {
        busy.delete(index);
      }
    },
    [afterFreshWrite, captureLine, emit, ensureFresh, isUnchangedSince]
  );

  /** "Apply expected value" arrow: writes the fresh amount for the line's current
   * inputs, only while that field still disagrees with it. */
  const applyComputed = React.useCallback(
    (index: number, field: ComputableField) => {
      const applyField = (current: InvoiceLineFormItem, fresh: InvoiceLineAmounts) => {
        const { isVatDocument: isVat, currencyCode: currency } = latestRef.current;
        const freshValue = fresh[field];
        if (freshValue == null || !computeMismatchFields(fresh, current, isVat).has(field)) {
          return current;
        }
        return updateLineField(current, field, roundToCurrencyPrecision(freshValue, currency));
      };
      runLineAction(index, applyField).catch(logActionError);
    },
    [runLineAction]
  );

  /** Apply a field's validation-issue expected value to the typed input. Amounts
   * are parsed and currency-rounded; non-numeric values pass through verbatim. */
  const applyExpectedValue = React.useCallback(
    (index: number, field: keyof InvoiceLine, expected: string) => {
      const parsed = parseDecimal(expected);
      const updated = applyUpdate(
        index,
        field,
        parsed == null ? expected : roundToCurrencyPrecision(parsed, currencyCode)
      );
      numericInputs.clearForField(index, field);
      // The applied value satisfies the issue; drop the line's now-stale issues
      // until the parent revalidates (the button suppresses the focus-clear).
      markLineCleared(index);
      runRecomputeNow(index, updated);
    },
    [applyUpdate, numericInputs.clearForField, currencyCode, markLineCleared, runRecomputeNow]
  );

  /** Per-line recompute button: normalize the line to the fresh result for its inputs. */
  const recomputeLine = React.useCallback(
    (index: number) => {
      const normalize = (current: InvoiceLineFormItem, fresh: InvoiceLineAmounts) => {
        const { isVatDocument: isVat, currencyCode: currency } = latestRef.current;
        return normalizeLine(current, fresh, isVat, currency);
      };
      runLineAction(index, normalize).catch(logActionError);
    },
    [runLineAction]
  );

  /** Toolbar "recompute all": await fresh results for every line, then commit in one
   * emit only the lines that did not change while waiting. */
  const recomputeAllLines = async () => {
    const busy = busyLinesRef.current;
    const lines = latestRef.current.value;
    const indices = lines.flatMap((_, index) => (busy.has(index) ? [] : [index]));
    if (indices.length === 0) return;
    indices.forEach(index => busy.add(index));
    try {
      const captured = indices.map(index => captureLine(lines[index]));
      const results = await Promise.all(indices.map(index => ensureFresh(index, lines[index])));
      if (!mountedRef.current || latestRef.current.disabled) return;
      const {
        value: currentLines,
        isVatDocument: isVat,
        currencyCode: currency
      } = latestRef.current;
      const next = [...currentLines];
      const written: { index: number; line: InvoiceLineFormItem; fresh: InvoiceLineAmounts }[] = [];
      indices.forEach((index, i) => {
        const fresh = results[i];
        const current = currentLines[index];
        if (!fresh || !current || !isUnchangedSince(index, current, captured[i])) return;
        const updated = normalizeLine(current, fresh, isVat, currency);
        if (updated === current) return;
        next[index] = updated;
        written.push({ index, line: updated, fresh });
      });
      if (written.length === 0) return;
      emit(next);
      written.forEach(({ index, line, fresh }) => afterFreshWrite(index, line, fresh));
    } finally {
      indices.forEach(index => busy.delete(index));
    }
  };

  /** Whether the latest result was computed for other inputs than the line shows,
   * buffered (not yet blurred) text included. */
  const isLineStale = (line: InvoiceLineFormItem, index: number) =>
    recompute.isStale(index, withNumericBuffer(line, numericInputs.getLineBuffer(index)));

  // Whether any line has a derivable field that is blank or disagrees, or a stale
  // result, so the toolbar "recompute all" button is disabled when there's nothing to do.
  const canRecomputeAll = value.some((line, index) => {
    const computed = recompute.getComputed(index);
    const hasMismatch = !!computed && computeMismatchFields(computed, line, isVatDocument).size > 0;
    return hasMismatch || isLineStale(line, index);
  });

  return (
    <div
      data-testid={getTestId({ testId, id })}
      className='@container/lines space-y-md sm:space-y-lg'
    >
      {issues.topLevelIssues.length > 0 && (
        <div className='bg-error-bg border border-error-border rounded-lg p-md'>
          <ErrorSummary issues={issues.topLevelIssues} />
        </div>
      )}

      <div>
        {/* Toolbar — sm+: inline right-aligned row; mobile: toggle row above,
            buttons split one row 50/50 with shortened labels. */}
        <div className='flex flex-col sm:flex-row sm:flex-wrap sm:items-center sm:justify-end gap-md mb-md'>
          <label
            className={cn(
              'flex items-center justify-end gap-sm text-xs font-medium text-foreground-muted cursor-pointer',
              { hidden: !isVatDocument }
            )}
          >
            <Toggle
              size='sm'
              checked={isUnitPriceInclVat}
              onChange={toggleUnitPriceInclVat}
              disabled={disabled}
              testId='isUnitPriceInclVat-toggle'
            />
            {t('unitPriceInclVatToggle')}
          </label>
          {/* Mobile: 50/50 row; sm+: wrapper dissolves so buttons rejoin the
              inline toolbar at natural width. Labels shorten below sm. */}
          <div className='flex gap-md sm:contents'>
            {onRecomputeLine && (
              <Button
                variant='default'
                outline='default'
                size='sm'
                onClick={() => recomputeAllLines().catch(logActionError)}
                disabled={disabled || !canRecomputeAll}
                aria-label={t('recomputeAll')}
                className='flex-1 min-w-0 sm:flex-none bg-white border font-semibold'
              >
                <Icon name='arrows-rotate' className='w-3.5 h-3.5 mr-sm' />
                <span className='sm:hidden'>{t('recomputeAllShort')}</span>
                <span className='hidden sm:inline'>{t('recomputeAll')}</span>
              </Button>
            )}
            <Button
              variant='default'
              outline='default'
              size='sm'
              onClick={addLine}
              disabled={disabled}
              aria-label={t('addLine')}
              className='flex-1 min-w-0 sm:flex-none bg-white border font-semibold'
            >
              <Icon name='plus' className='w-3.5 h-3.5 mr-sm' />
              <span className='sm:hidden'>{t('addLineShort')}</span>
              <span className='hidden sm:inline'>{t('addLine')}</span>
            </Button>
          </div>
        </div>

        {value.length > 0 ? (
          <div>
            {scrollerRef ? (
              // Virtualized path: used inside the ScanEditor panel where the
              // list can grow large. Requires a custom scroll container ref.
              <Virtuoso
                ref={virtuosoRef}
                customScrollParent={scrollerRef.current ?? undefined}
                increaseViewportBy={300}
                totalCount={value.length}
                itemContent={index => {
                  const line = value[index];
                  if (!line) return null;
                  return (
                    <div data-hover-field={`invoiceLines.${index}`} className='mb-md'>
                      <InvoiceLineRow
                        line={line}
                        index={index}
                        vatRateOptions={vatRateOptions}
                        disabled={disabled}
                        isUnitPriceInclVat={isUnitPriceInclVat}
                        issues={issues}
                        numericBuffer={numericInputs.getLineBuffer(index)}
                        computed={recompute.getComputed(index)}
                        isStale={isLineStale(line, index)}
                        onUpdateField={updateLine}
                        onNumericChange={handleNumericChange}
                        onNumericBlur={numericInputs.handleBlur}
                        onDelete={deleteLine}
                        onApplyComputed={onRecomputeLine ? applyComputed : undefined}
                        onApplyExpected={applyExpectedValue}
                        onRecompute={onRecomputeLine ? recomputeLine : undefined}
                        onFocusLine={markLineCleared}
                        extraFields={lineFields}
                        onPatchLine={patchLine}
                      />
                    </div>
                  );
                }}
              />
            ) : (
              // Plain list: used inside modals where the line count is small
              // and virtualization causes headless-browser rendering issues.
              <div className='space-y-md'>
                {value.map((line, index) => (
                  <div
                    key={line.key ?? line.id ?? index}
                    data-hover-field={`invoiceLines.${index}`}
                  >
                    <InvoiceLineRow
                      line={line}
                      index={index}
                      vatRateOptions={vatRateOptions}
                      disabled={disabled}
                      isUnitPriceInclVat={isUnitPriceInclVat}
                      issues={issues}
                      numericBuffer={numericInputs.getLineBuffer(index)}
                      computed={recompute.getComputed(index)}
                      isStale={isLineStale(line, index)}
                      onUpdateField={updateLine}
                      onNumericChange={handleNumericChange}
                      onNumericBlur={numericInputs.handleBlur}
                      onDelete={deleteLine}
                      onApplyComputed={onRecomputeLine ? applyComputed : undefined}
                      onApplyExpected={applyExpectedValue}
                      onRecompute={onRecomputeLine ? recomputeLine : undefined}
                      onFocusLine={markLineCleared}
                      extraFields={lineFields}
                      onPatchLine={patchLine}
                    />
                  </div>
                ))}
              </div>
            )}
          </div>
        ) : (
          <div className='text-center py-2xl text-foreground-muted'>
            <Icon name='box' className='w-12 h-12 mx-auto mb-md opacity-50' />
            <p className='text-sm'>{t('noLines')}</p>
          </div>
        )}
      </div>
    </div>
  );
};

InvoiceLinesForm.displayName = 'InvoiceLinesForm';
